import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { ACCOUNT_PRO_ROLES } from '../../../constants/account-roles';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { getDiscountPrice } from '../../../util/promo-codes';

/**
 * @enum PRO_REGISTRATION_VARIANT
 *
 * @type {{VARIANT_A: string, VARIANT_B: string}}
 */
export const PRO_REGISTRATION_VARIANT = {
  VARIANT_A: 'VARIANT_A', // No credit card
  VARIANT_B: 'VARIANT_B', // Credit card
};

export const PRO_REGISTRATION_PERSONA_VALUES = {
  PRO: 'pro',
  SCHOOL: 'school',
  CAMP: 'camp',
  CHURCH: 'church',
  SPORTS: 'sports',
  GREEK: 'greek',
  EVENTS: 'events',
  VENUE: 'venue',
  HR: 'hr',
};

/**
 * Pro registration personas.
 *
 * @type {[{label: string, value: string}]}
 */
export const PRO_REGISTRATION_PERSONAS = [
  {
    label: 'Professional Photographer',
    value: PRO_REGISTRATION_PERSONA_VALUES.PRO,
    role: ACCOUNT_PRO_ROLES.PHOTOGRAPHER,
  },
  {
    label: 'School Administrator',
    value: PRO_REGISTRATION_PERSONA_VALUES.SCHOOL,
    role: ACCOUNT_PRO_ROLES.SCHOOL_ADMIN,
  },
  {
    label: 'Camp Director',
    value: PRO_REGISTRATION_PERSONA_VALUES.CAMP,
    role: ACCOUNT_PRO_ROLES.CAMP_ADMIN,
  },
  {
    label: 'Church Administrator',
    value: PRO_REGISTRATION_PERSONA_VALUES.CHURCH,
    role: ACCOUNT_PRO_ROLES.CHURCH_ADMIN,
  },
  {
    label: 'Sports Director',
    value: PRO_REGISTRATION_PERSONA_VALUES.SPORTS,
    role: ACCOUNT_PRO_ROLES.SPORTS_ADMIN,
  },
  {
    label: 'Greek Officer',
    value: PRO_REGISTRATION_PERSONA_VALUES.GREEK,
    role: ACCOUNT_PRO_ROLES.GREEK_ADMIN,
  },
  {
    label: 'Event Organizer',
    value: PRO_REGISTRATION_PERSONA_VALUES.EVENTS,
    role: ACCOUNT_PRO_ROLES.EVENT_ORGANIZER,
  },
  {
    label: 'Venue Owner',
    value: PRO_REGISTRATION_PERSONA_VALUES.VENUE,
    role: ACCOUNT_PRO_ROLES.EVENT_ORGANIZER,
  },
  {
    label: 'Corporate Event Planner',
    value: PRO_REGISTRATION_PERSONA_VALUES.HR,
    role: ACCOUNT_PRO_ROLES.EVENT_ORGANIZER,
  },
];

/**
 * Returns the first monthly PhotoManagerSubscriptionItem with a trial.
 *
 * @param {Array} photoManagerSubscriptionItems The PhotoManagerSubscriptionItems.
 * @returns {null|*}
 */
export const getDefaultTrialPhotoManagerSubscriptionItem = photoManagerSubscriptionItems => {
  if (!photoManagerSubscriptionItems?.length) {
    return null;
  }

  return photoManagerSubscriptionItems.find(photoManagerSubscriptionItem => {
    return (
      photoManagerSubscriptionItem.billing_period ===
      PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY
    );
  });
};

/**
 * Returns the data string to use on clientSetStore mutation for the "pro_registration_mode" key.
 *
 * @param {Object} values - The registration form's values.
 * @returns {string}
 */
export const getProRegistrationStoreKeyValue = values => {
  if (values.requires_payment && values.stripe_token) {
    return 'CREDIT_CARD_PROMO';
  }

  if (!values.requires_payment && values.stripe_token) {
    return 'CREDIT_CARD';
  }

  return 'NO_CREDIT_CARD';
};

export const PRO_REGISTRATION_STEP = {
  FREE_TRIAL: 'FREE_TRIAL',
  EVENT_SKU_SELECTION: 'EVENT_SKU_SELECTION',
  PHOTO_MANAGER_SUBSCRIPTION_SELECTION: 'PHOTO_MANAGER_SUBSCRIPTION_SELECTION',
  PHOTO_FINDER_CREDITS_SELECTION: 'PHOTO_FINDER_CREDITS_SELECTION',
  DETAILS: 'DETAILS',
  PIN_VERIFICATION: 'PIN_VERIFICATION',
  PASSWORD_CREATION: 'PASSWORD_CREATION',
  CART: 'CART',
  FUNDRAISER_MARKETING_COMMITMENT: 'FUNDRAISER_MARKETING_COMMITMENT',
  PLIC_LOGIN: 'PLIC_LOGIN',
};

export const PHOTO_FINDER_FEATURES = [
  'Automated photo sorting by facial & jersey recognition',
  'Automated photo delivery through text and app alerts',
  'Smart dashboard to track delivery analytics',
  'Stream photos to friends and family',
  'Send personalized cards',
];

/**
 * Calculates the photo manager subscription price based on promo code.
 *
 * @param {Object} opts List of options.
 * @param {Object} opts.photoManagerSubscriptionItem The photo manager subscription item/record.
 * @param {Object} opts.accountPromoCode An account promo code.
 * @returns {Number}
 */
export const getPhotoManagerSubscriptionPrice = ({
  photoManagerSubscriptionItem,
  accountPromoCode,
}) => {
  if (
    !accountPromoCode ||
    ![
      ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
      ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
      ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
    ].includes(accountPromoCode.type)
  ) {
    // Only parse here proper promo code
    return photoManagerSubscriptionItem.price?.value ?? 0;
  }
  // Adds 1 to the value if it must be divided, since the value would result in having a decimal number
  // If the value were empty (0) the decimal would still be ignored, so it's alright if we get 0.5.
  // eslint-disable-next-line no-nested-ternary
  return accountPromoCode.type ===
    ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE
    ? photoManagerSubscriptionItem?.price?.value / 2
    : getDiscountPrice(
        photoManagerSubscriptionItem?.price?.value,
        accountPromoCode,
      );
};

/**
 * Calculates the photo finder subscription price based on promo code.
 *
 * @param {Object} opts List of options.
 * @param {Object} opts.photoFinderSubscriptionItem The photo manager subscription item/record.
 * @param {Object} opts.accountPromoCode An account promo code.
 * @returns {Number}
 */
export const getPhotoFinderSubscriptionPrice = ({
  photoFinderSubscriptionItem,
  accountPromoCode,
}) => {
  if (
    !accountPromoCode ||
    ![
      ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_MONTHLY,
      ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_YEARLY,
    ].includes(accountPromoCode.type)
  ) {
    // Only parse here proper promo code
    return photoFinderSubscriptionItem?.price?.value ?? 0;
  }

  // Adds 1 to the value if it must be divided, since the value would result in having a decimal number
  // If the value were empty (0) the decimal would still be ignored, so it's alright if we get 0.5.
  // eslint-disable-next-line no-nested-ternary
  return getDiscountPrice(
    photoFinderSubscriptionItem?.price?.value,
    accountPromoCode,
  );
};
